import moment from 'moment'
import { useEffect, useState } from 'react'

import { useGetUnitTrafficSignalStrength } from '@apps-common/apis/portal/unit'
import { TRAFFIC_SIGNAL_STRENGTH_DEFAULT_TAKE } from '@apps-common/config/filter'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { IUnitTrafficModel } from '@apps-common/types/portal/unit'

import { ModalBody, ModalCloseButton, ModalContainer, ModalFooter, ModalHeader, ModalHeaderContainer } from '../modal'

import { VertSpacerMd } from '@apps-common/components/layout'
import { SignalStrengthChart } from './SignalStrengthChart'
import { SignalStrengthKpi } from './SignalStrengthKpi'

export interface ISignalStrengthModalProps {
  ipAddress: string
  imei?: string
}

export const SignalStrengthModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: ISignalStrengthModalProps
}) => {
  const { ipAddress, imei } = componentProps
  const [signals, setSignals] = useState<IUnitTrafficModel[]>([])
  const [skip, setSkip] = useState(0)

  const {
    isLoading: signalsIsLoading,
    isFetching: signalsIsFetching,
    isRefetching: signalsIsRefetching,
    isError: signalsIsError,
    data: signalsData,
    refetch: signalsRefetch
  } = useGetUnitTrafficSignalStrength({
    config: {
      enabled: !!ipAddress
    },
    identifiers: `${ipAddress},${imei}`,
    skip,
    take: 25
  })

  useEffect(() => {
    if (!signalsIsLoading) {
      setSignals(signalsData || [])
    }
  }, [signalsData])

  const loading = (signalsIsLoading || signalsIsError) && !signalsData

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0 !max-w-[700px]' onDismiss={onDismiss}>
      <ModalHeaderContainer classes='!border-none'>
        <div className='flex flex-col items-start justify-between gap-[10px]'>
          <div className='flex items-center justify-between w-full'>
            <ModalHeader title='Signal strength' titleClasses='capitalize' />
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
          <SignalStrengthKpi loading={loading} data={signalsData} />
        </div>
        <div className='jost-light text-[0.9rem] text-center mt-[10px] text-text'>Signal strength by event</div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[140px] !pb-[100px] sm:!pb-[70px] !pb-0 w-full !px-1 sm:!px-4 md:!px-6'>
        {!loading && (!signals || signals.length === 0) && (
          <div className='!pt-[120px] !pb-[60px] sm:!pb-[60px] absolute top-0 left-0 bg-surface-2 text-text flex items-center justify-center w-full h-full z-[2]'>
            <div className='bg-surface px-[20px] py-[10px] jost-light rounded-full'>No Signals</div>
          </div>
        )}
        <SignalStrengthChart loading={loading} classes='bg-surface p-4 rounded-xl sm:min-w-[450px] sm:min-h-[350px]' data={signals} />
        <VertSpacerMd />
      </ModalBody>
      <ModalFooter>
        <div className='flex md:hidden items-center justify-center gap-[10px]'>
          <div className='jost-light bg-surface text-text border border-border text-[0.8em] sm:text-[0.9em] min-w-[120px] rounded-full px-4 py-1 flex items-center justify-center'>
            {!loading && signalsData && signalsData.length > 0 && (
              <>
                <div className='hidden sm:inline-block'>
                  <span className='jost'>
                    {signalsData && signalsData.length > 0 && moment(signalsData[signalsData.length - 1]?.timeStamp).format('Do MMM YY')}
                  </span>
                  {' to '}
                  <span className='jost'>
                    {signalsData && signalsData.length > 0 && moment(signalsData[0]?.timeStamp).format('Do MMM YY')}
                  </span>
                </div>
                <div className='inline-block sm:hidden'>
                  <span className='jost'>
                    {signalsData && signalsData.length > 0 && moment(signalsData[signalsData.length - 1]?.timeStamp).format('Do MMM')}
                  </span>
                  {' to '}
                  <span className='jost'>
                    {signalsData && signalsData.length > 0 && moment(signalsData[0]?.timeStamp).format('Do MMM')}
                  </span>
                </div>
              </>
            )}
            {(loading || !signalsData || signalsData.length === 0) && (
              <span className='jost'>
                <commonIcons.dash />
              </span>
            )}
          </div>
        </div>
        <div className='flex items-end justify-between gap-[5px]'>
          <button
            type='button'
            aria-label='Close filter'
            disabled={loading || !signalsData || signalsData.length === 0}
            className='bg-surface text-text border border-border rounded-full flex items-center justify-center pr-[10px] p-[2px] md:p-[5px] gap-[5px] disabled:opacity-30 text-[0.8em] jost-light'
            onClick={() => {
              setSkip(skip + TRAFFIC_SIGNAL_STRENGTH_DEFAULT_TAKE)
            }}
          >
            <div className='text-[1.2em] bg-surface-2 w-[30px] h-[30px] rounded-full flex items-center justify-center'>
              <commonIcons.arrowRightShort className='rotate-180' />
            </div>
            Older
          </button>
          <div className='hidden md:flex flex-col items-center justify-center gap-[10px]'>
            <div className='jost-light bg-surface text-text border border-border text-[0.8em] sm:text-[0.9em] min-w-[120px] h-[40px] rounded-full px-[5px] sm:px-[20px] py-[10px] flex items-center justify-center gap-[5px] grow sm:grow-0'>
              {!loading && signalsData && signalsData.length > 0 && (
                <>
                  <div className='hidden sm:inline-block'>
                    <span className='jost'>
                      {signalsData && signalsData.length > 0 && moment(signalsData[signalsData.length - 1]?.timeStamp).format('Do MMM YY')}
                    </span>
                    {' to '}
                    <span className='jost'>
                      {signalsData && signalsData.length > 0 && moment(signalsData[0]?.timeStamp).format('Do MMM YY')}
                    </span>
                  </div>
                  <div className='inline-block sm:hidden'>
                    <span className='jost'>
                      {signalsData && signalsData.length > 0 && moment(signalsData[signalsData.length - 1]?.timeStamp).format('Do MMM')}
                    </span>
                    {' to '}
                    <span className='jost'>
                      {signalsData && signalsData.length > 0 && moment(signalsData[0]?.timeStamp).format('Do MMM')}
                    </span>
                  </div>
                </>
              )}
              {(loading || !signalsData || signalsData.length === 0) && (
                <span className='jost'>
                  <commonIcons.dash />
                </span>
              )}
            </div>
          </div>
          <div className='relative flex items-center justify-end gap-[5px]'>
            {skip > TRAFFIC_SIGNAL_STRENGTH_DEFAULT_TAKE && (
              <button
                type='button'
                disabled={loading || skip < TRAFFIC_SIGNAL_STRENGTH_DEFAULT_TAKE}
                className='bg-surface text-text border border-border rounded-full flex items-center justify-center p-[2px] md:p-[5px] gap-[5px] disabled:opacity-50 text-[0.8em] jost-light'
                onClick={() => {
                  setSkip(0)
                }}
              >
                <div className='text-[1.2em] bg-secondary-content/10 w-[30px] h-[30px] rounded-full flex items-center justify-center'>
                  <commonIcons.caretDoubleLeftBold className='rotate-180' />
                </div>
              </button>
            )}
            <button
              type='button'
              disabled={loading || skip < TRAFFIC_SIGNAL_STRENGTH_DEFAULT_TAKE}
              className='bg-surface text-text border border-border rounded-full flex items-center justify-center pl-[10px] p-[2px] md:p-[5px] gap-[5px] disabled:opacity-50 text-[0.8em] jost-light'
              onClick={() => {
                let newSkip = skip - TRAFFIC_SIGNAL_STRENGTH_DEFAULT_TAKE

                if (newSkip < 0) {
                  newSkip = 0
                }
                setSkip(newSkip)
              }}
            >
              Newer
              <div className='text-[1.2em] bg-secondary-content/10 w-[30px] h-[30px] rounded-full flex items-center justify-center'>
                <commonIcons.arrowRightShort />
              </div>
            </button>
          </div>
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
